import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProducts } from "@/models/product";
import { useSelectionStore, type WindowFabricColor } from "@/store/selection";
import { useTotalPrice } from "@/models/price";
import { resolveWindowDefaults, useColorHexMap } from "@/models/color";
import { sortProductIdsByLocation } from "@/lib/sortProductsByLocation";
import { WindowCard } from "@/components/organisms/WindowCard/WindowCard";
import { getDisplayAmount } from "@/lib/getDisplayAmount";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import type { DealProductWithLocation } from "@/models/deal/deal.types";

// Scroll-into-view delay matches the card's `duration-400` CSS transition so
// the scroll fires after the next card finishes expanding.
const CARD_TRANSITION_MS = 420;

export function ColorStep() {
    const { t } = useTranslation();
    const {
        selectedCategory,
        selectedTypeId,
        selectedProductIds,
        dealProducts,
        windowSelections,
        setWindowSelection,
    } = useSelectionStore();
    const { products, loading } = useProducts(selectedCategory);
    const { productPrices } = useTotalPrice();
    const { data: pageContent } = useSunProtectionPageContent();
    const pricesIncludeVat = pageContent?.pricesIncludeVat;

    const sortedProductIds = useMemo(
        () => sortProductIdsByLocation(selectedProductIds, dealProducts),
        [selectedProductIds, dealProducts],
    );

    // Resolve each window's outerColor id → hex so we can default the sun
    // protection frame colour to the matching CMS frameColor by hex.
    const outerColorIds = useMemo(
        () => dealProducts.map((p) => p.outerColorId),
        [dealProducts],
    );
    const { hexById: outerHexById } = useColorHexMap(outerColorIds);

    const product = useMemo(
        () => products.find((p) => p.id === selectedTypeId) ?? null,
        [products, selectedTypeId],
    );
    const [openWindowIds, setOpenWindowIds] = useState<Set<string>>(() => {
        const firstUnconfigured = sortedProductIds.find(
            (id) => !windowSelections[id],
        );
        const toOpen = firstUnconfigured ?? sortedProductIds[0];
        return toOpen ? new Set([toOpen]) : new Set();
    });

    // Pre-select fabric and frame on a window only when it becomes the open
    // card — matching the pre-branch behaviour where collapsed cards stay
    // untouched until the user expands them. Both colours follow the same
    // priority: 1. hex match → 2. previous from another window → 3. first.
    //
    // Reads `windowSelections` via `getState()` so external writes (fabric
    // clicks, useRestoreOrder) don't re-trigger this loop. Windows whose
    // outerColor hex hasn't resolved yet are skipped — otherwise a "first"
    // default would beat the (later) outer-hex match.
    useEffect(() => {
        if (!product) return;
        const stored = useSelectionStore.getState().windowSelections;
        // Walk windows in deal order — not insertion order — so the
        // "previous color" the user inherits is the first earlier window's
        // choice as it appears on screen, not whichever entry happened to
        // be written first by useRestoreOrder / click ordering.
        const orderedConfigured = sortedProductIds
            .map((id) => stored[id])
            .filter((s): s is WindowFabricColor => !!s);
        const previousFabricColorId =
            orderedConfigured.find((s) => s.colorId)?.colorId ?? null;
        const previousFrameColorId =
            orderedConfigured.find((s) => s.frameColorId)?.frameColorId ?? null;
        const additions: Record<string, WindowFabricColor> = {};
        for (const windowId of openWindowIds) {
            if (stored[windowId]) continue;
            const dealProduct = dealProducts.find((p) => p.id === windowId);
            const outerColorId = dealProduct?.outerColorId;
            if (outerColorId && !outerHexById[outerColorId]) continue;
            const outerHex = outerColorId
                ? outerHexById[outerColorId]
                : undefined;

            const defaults = resolveWindowDefaults(
                product,
                outerHex,
                previousFabricColorId,
                previousFrameColorId,
            );
            if (!defaults) continue;
            additions[windowId] = { windowId, ...defaults };
        }
        if (Object.keys(additions).length === 0) return;
        useSelectionStore.setState((state) => ({
            windowSelections: { ...state.windowSelections, ...additions },
        }));
    }, [product, openWindowIds, dealProducts, outerHexById, sortedProductIds]);

    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

    function scrollToCard(windowId: string) {
        const el = cardRefs.current[windowId];
        if (!el) return;
        setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, CARD_TRANSITION_MS);
    }

    // Just open the next card. The preselect effect picks defaults for it
    // (matching its own outer colour, with the previous fabric/frame from
    // already-configured windows as a fallback) — don't copy the current
    // window's selection wholesale.
    function handleNext(windowId: string) {
        const currentIndex = sortedProductIds.indexOf(windowId);
        const isLast = currentIndex === sortedProductIds.length - 1;

        if (isLast) {
            setOpenWindowIds((prev) => {
                const next = new Set(prev);
                next.delete(windowId);
                return next;
            });
            return;
        }

        const nextWindowId = sortedProductIds[currentIndex + 1];
        setOpenWindowIds((prev) => {
            const next = new Set(prev);
            next.delete(windowId);
            next.add(nextWindowId);
            return next;
        });
        scrollToCard(nextWindowId);
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-4">
                <div className="h-8 w-64 rounded bg-gray-100 animate-pulse mx-auto" />
                <div className="h-4 w-80 rounded bg-gray-100 animate-pulse mx-auto" />
                {[1, 2].map((i) => (
                    <div
                        key={i}
                        className="h-40 rounded-xl bg-gray-100 animate-pulse"
                    />
                ))}
            </div>
        );
    }

    if (!selectedTypeId) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400">
                {t("colorStep.noTypeSelected")}
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400">
                {t("colorStep.noTypeSelected")}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 leading-tight">
                    {t("colorStep.title")}
                </h1>
                <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                    {t("colorStep.subtitle")}
                </p>
            </div>

            <div className="flex flex-col items-stretch">
                <div className="flex flex-col gap-3 min-w-0">
                    {sortedProductIds
                        .map((windowId) => ({
                            windowId,
                            dealProduct: dealProducts.find(
                                (p) => p.id === windowId,
                            ),
                            priceEntry: productPrices.find(
                                (p) => p.productId === windowId,
                            ),
                        }))
                        .filter(
                            (
                                item,
                            ): item is typeof item & {
                                dealProduct: DealProductWithLocation;
                            } => !!item.dealProduct,
                        )
                        .map(({ windowId, dealProduct, priceEntry }, idx) => (
                            <WindowCard
                                key={windowId}
                                windowId={windowId}
                                dealProduct={dealProduct}
                                product={product}
                                storedSelection={windowSelections[windowId]}
                                isOpen={openWindowIds.has(windowId)}
                                isLast={idx === sortedProductIds.length - 1}
                                onToggle={() =>
                                    setOpenWindowIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(windowId)) {
                                            next.delete(windowId);
                                        } else {
                                            next.add(windowId);
                                        }
                                        return next;
                                    })
                                }
                                onColorSelected={(
                                    colorId,
                                    fabricType,
                                    frameColorId,
                                ) => {
                                    const fabric = product.fabricColors.find(
                                        (c) => c.id === colorId,
                                    );
                                    if (!fabric?.id) return;
                                    const frame = frameColorId
                                        ? product.frameColors.find(
                                              (c) => c.id === frameColorId,
                                          )
                                        : null;
                                    const previous = windowSelections[windowId];
                                    setWindowSelection({
                                        windowId,
                                        fabricType,
                                        colorId: fabric.id,
                                        colorHex: fabric.hexColor ?? null,
                                        colorName: fabric.name ?? null,
                                        pricePerCm2: fabric.price ?? null,
                                        frameColorId: frame?.id ?? null,
                                        frameColorName: frame?.name ?? null,
                                        frameColorHex: frame?.hexColor ?? null,
                                        controlType:
                                            previous?.controlType ??
                                            (product.productType ===
                                            "SUN_PROTECTION"
                                                ? "REMOTE"
                                                : null),
                                    });
                                }}
                                onControlTypeChange={(controlType) => {
                                    const previous = windowSelections[windowId];
                                    if (!previous) return;
                                    setWindowSelection({
                                        ...previous,
                                        windowId,
                                        controlType,
                                    });
                                }}
                                onNext={() => handleNext(windowId)}
                                cardRef={(el) => {
                                    cardRefs.current[windowId] = el;
                                }}
                                price={
                                    getDisplayAmount(
                                        pricesIncludeVat,
                                        priceEntry?.total,
                                    ) ?? null
                                }
                                discountedPrice={
                                    getDisplayAmount(pricesIncludeVat, {
                                        netAmount:
                                            priceEntry?.total
                                                .discountedNetAmount,
                                        grossAmount:
                                            priceEntry?.total
                                                .discountedGrossAmount,
                                    }) ?? null
                                }
                            />
                        ))}
                </div>
            </div>
        </div>
    );
}
